import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { imgUrl } from "@/components/site/WorkGallery";
import { PageHeader, Empty } from "@/components/portal/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const UploadForm = ({ onUploaded }) => {
  const api = useApi();
  const fileRef = useRef(null);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("work");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    const files = Array.from(fileRef.current?.files || []);
    if (!files.length) return toast.error("Choose at least one photo");
    setBusy(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name.replace(/\.[^.]+$/, ""));
      fd.append("caption", caption);
      fd.append("category", category);
      try {
        onUploaded(await api("post", "/gallery", fd));
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err)}`);
      }
    }
    toast.success("Upload finished — photos are live on the website");
    fileRef.current.value = "";
    setTitle("");
    setCaption("");
    setBusy(false);
  };

  return (
    <form onSubmit={upload} className="mb-8 grid gap-3 rounded-2xl border border-[#E6E2DA] bg-white p-5 sm:grid-cols-2 lg:grid-cols-5" data-testid="gallery-upload-form">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple data-testid="gallery-file-input"
        className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#0E1F3F] file:px-4 file:py-2 file:text-white lg:col-span-2" />
      <Input placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="gallery-title-input" />
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="work">Our Work gallery</SelectItem><SelectItem value="special">Specials / flyers</SelectItem></SelectContent>
      </Select>
      <button disabled={busy} data-testid="gallery-upload-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F2B91C] px-4 py-2 text-sm font-bold text-[#0E1F3F] hover:bg-[#D9A30F] disabled:opacity-60"><Upload size={15} />{busy ? "Uploading…" : "Upload"}</button>
      <Input className="sm:col-span-2 lg:col-span-5" placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption-input" />
    </form>
  );
};

export default function GalleryAdmin() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("work");
  useEffect(() => {
    api("get", "/gallery").then(setItems);
  }, [api]);

  const remove = async (i) => {
    if (!window.confirm(`Remove "${i.title}" from the website?`)) return;
    try {
      await api("delete", `/gallery/${i.id}`);
      setItems(items.filter((x) => x.id !== i.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const shown = (items || []).filter((i) => i.category === tab);

  return (
    <div data-testid="gallery-admin-page">
      <PageHeader eyebrow="Website" title="Photo gallery" />
      <UploadForm onUploaded={(d) => setItems((cur) => [d, ...(cur || [])])} />
      <div className="mb-5 flex gap-2">
        {[["work", "Our Work"], ["special", "Specials / flyers"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`gallery-tab-${k}`} className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${tab === k ? "border-[#0E1F3F] bg-[#0E1F3F] text-white" : "border-[#E6E2DA] bg-white"}`}>{l} · {(items || []).filter((i) => i.category === k).length}</button>
        ))}
      </div>
      {items && !shown.length && <Empty text="No photos in this section yet." />}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        {shown.map((i) => (
          <div key={i.id} className="overflow-hidden rounded-xl border border-[#E6E2DA] bg-white" data-testid={`gallery-admin-item-${i.id}`}>
            <img src={imgUrl(i.id)} alt={i.title} loading="lazy" className="aspect-square w-full object-cover" />
            <div className="flex items-start justify-between gap-2 p-3">
              <div className="min-w-0"><p className="truncate text-sm font-semibold">{i.title}</p><p className="text-xs text-[#8C877D]">{fmtDate(i.created_at)}</p></div>
              <button onClick={() => remove(i)} data-testid={`gallery-delete-${i.id}`} className="text-[#8C877D] hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
